import {
  RECAPTURE_CAMPAIGNS,
  RECAPTURE_CAMPAIGN_DRAFTS,
  RECAPTURE_SCHEDULES,
  RECAPTURE_TEMPLATES,
  RECAPTURE_EMAIL_TEMPLATES,
  RECAPTURE_EMAIL_SETTINGS,
  RECAPTURE_CALL_TEMPLATES,
  RECAPTURE_SMS_PROFILES,
  PATIENT_GROUP_RESOURCE_LIST,
  CLEAR_RECAPTURE_ERRORS,
} from '../actions/patientRecapture';

import {
  CONFLICT_CODES,
  extractRecaptureEmailUserError,
  recaptureEmailConflictCode,
} from '../components/PatientRecapture/recaptureEmail';

const initialState = {
  // Campaigns
  campaigns: [],
  campaignsLoading: false,
  campaignsError: null,
  campaignSaving: false,
  campaignError: null,

  // Campaign Drafts (ENG-3285)
  campaignDrafts: [],
  campaignDraftsLoading: false,
  campaignDraftsError: null,
  campaignDraftSaving: false,
  campaignDraftError: null,

  // Schedules
  schedules: [],
  schedulesLoading: false,
  schedulesRequestId: null,
  schedulesError: null,
  scheduleSaving: false,
  scheduleError: null,

  // Templates
  templates: [],
  templatesLoading: false,
  templatesRequestId: null,
  templatesError: null,
  templateSaving: false,
  templateError: null,

  // Email templates (ENG-3105 U1a)
  emailTemplates: [],
  emailTemplatesLoading: false,
  emailTemplatesError: null,
  emailTemplatesRequestId: null,
  emailTemplateSaving: false,
  emailTemplateError: null,
  // The manager owns the selected detail payload and editor visibility. Redux
  // retains only request ownership so a late detail terminal cannot replace
  // the current editor's error/conflict state.
  emailTemplateDetailRequestId: null,
  emailTemplateConflictCode: null,
  emailMemberPreviews: {},
  // Kept separately from the rendered cache so revision eviction can reject a
  // late terminal response before the replacement card request has started.
  emailMemberPreviewRequests: {},
  emailSettings: null,
  emailSettingsLoading: false,
  emailSettingsRequestId: null,
  emailSettingsSaving: false,
  emailSettingsError: null,
  // Call Templates
  callTemplates: [],
  callPromptProfiles: [],
  callTemplatesLoading: false,
  callTemplatesRequestId: null,
  callTemplatesError: null,
  callTemplateSaving: false,
  callTemplateError: null,

  // Code Groups
  codeGroups: [],
  codeGroupsLoading: false,
  codeGroupsRequestId: null,
  codeGroupsError: null,
  codeGroupSaving: false,
  codeGroupError: null,
  currentCodeGroup: null,
  currentCodeGroupLoading: false,

  // Appointment Type Groups
  appointmentTypeGroups: [],
  appointmentTypeGroupsLoading: false,
  appointmentTypeGroupsRequestId: null,
  appointmentTypeGroupsError: null,
  appointmentTypeGroupSaving: false,
  appointmentTypeGroupError: null,
  currentAppointmentTypeGroup: null,
  currentAppointmentTypeGroupLoading: false,

  // SMS Profiles
  smsProfiles: [],
  smsProfilesLoading: false,
  smsProfilesRequestId: null,
  smsProfilesError: null,
};

const extractError = (action) => {
  return action?.payload?.response?.data?.error
    || action?.payload?.response?.data?.errors?.[0]
    || 'An unexpected error occurred';
};

const extractEmailTemplateMutationError = (action) => {
  const { code, field } = action?.payload?.response?.data || {};

  if (code === 'invalid_state' && field === 'reply_to') {
    return 'The template\'s Reply-To address is no longer allowed. Update it or ask an administrator to allowlist it, then try again.';
  }

  if (code === 'invalid_state' && !field) {
    return 'This template is used by an active schedule. Deactivate or unlink the schedule before deactivating the template.';
  }

  return extractRecaptureEmailUserError(action);
};

const withEmailMutationFailure = (state, action, errorKey, conflictKey) => {
  const conflict = recaptureEmailConflictCode(action);
  return {
    ...state,
    [errorKey]: conflict === CONFLICT_CODES.STALE_REVISION ? null : extractEmailTemplateMutationError(action),
    [conflictKey]: conflict,
  };
};

const emailTemplateDetailRequestIsCurrent = (state, action) => {
  const actionRequestId = action.meta?.requestId;
  const currentRequestId = state.emailTemplateDetailRequestId;
  return actionRequestId == null
    ? currentRequestId == null
    : actionRequestId === currentRequestId;
};

const requestCanBegin = (currentRequestId, action) => {
  const actionRequestId = action.meta?.requestId;
  if (actionRequestId == null) return currentRequestId == null;
  return currentRequestId == null || actionRequestId >= currentRequestId;
};

const emailTemplateListRequestIsCurrent = (state, action) => {
  const actionRequestId = action.meta?.requestId;
  const currentRequestId = state.emailTemplatesRequestId;
  return actionRequestId == null
    ? currentRequestId == null
    : actionRequestId === currentRequestId;
};

const emailSettingsRequestIsCurrent = (state, action) => {
  const actionRequestId = action.meta?.requestId;
  const currentRequestId = state.emailSettingsRequestId;
  return actionRequestId == null
    ? currentRequestId == null
    : actionRequestId === currentRequestId;
};

const revisedEmailTemplateIds = (previousTemplates, nextTemplates) => {
  const previousRevisionIds = Object.fromEntries(previousTemplates.map((template) => [
    template.id, template.current_revision_id,
  ]));
  const revisedTemplateIds = nextTemplates
    .filter((template) => (
      Object.prototype.hasOwnProperty.call(previousRevisionIds, template.id)
      && previousRevisionIds[template.id] !== template.current_revision_id
    ))
    .map((template) => template.id);

  return revisedTemplateIds;
};

const evictRevisedEmailMemberPreviews = (emailMemberPreviews, previousTemplates, nextTemplates) => {
  const revisedTemplateIds = revisedEmailTemplateIds(previousTemplates, nextTemplates);
  if (revisedTemplateIds.length === 0) return emailMemberPreviews;

  const nextPreviews = { ...emailMemberPreviews };
  revisedTemplateIds.forEach((templateId) => delete nextPreviews[templateId]);
  return nextPreviews;
};

const invalidateRevisedEmailMemberPreviewRequests = (requests, previousTemplates, nextTemplates) => {
  const revisedTemplateIds = revisedEmailTemplateIds(previousTemplates, nextTemplates);
  if (revisedTemplateIds.length === 0) return requests;

  const nextRequests = { ...requests };
  revisedTemplateIds.forEach((templateId) => {
    const template = nextTemplates.find((item) => item.id === templateId);
    nextRequests[templateId] = {
      revisionId: template.current_revision_id,
      requestId: null,
    };
  });
  return nextRequests;
};

const emailMemberPreviewRequestIsCurrent = (state, action) => {
  const templateId = action.meta?.templateId;
  const expected = state.emailMemberPreviewRequests?.[templateId];
  if (!expected) return true;
  return expected.requestId === action.meta?.requestId
    && expected.revisionId === action.meta?.revisionId;
};

const emailMemberPreviewRequestCanBegin = (state, action) => {
  const templateId = action.meta?.templateId;
  const expected = state.emailMemberPreviewRequests?.[templateId];
  if (!expected) return true;
  if (expected.revisionId !== action.meta?.revisionId) return false;
  return requestCanBegin(expected.requestId, action);
};

const findPatientGroupResource = (actionType) => (
  PATIENT_GROUP_RESOURCE_LIST.find((resource) => Object.values(resource.actionTypes).includes(actionType))
);

const reducePatientGroupResource = (state, action) => {
  const resource = findPatientGroupResource(action.type);
  if (!resource) return null;

  const { actionTypes } = resource;
  const { state: resourceState } = resource;
  switch (action.type) {
    case actionTypes.FETCH_BEGIN:
      if (!requestCanBegin(state[resourceState.collectionRequestId], action)) return state;
      return {
        ...state,
        [resourceState.collectionRequestId]: action.meta?.requestId ?? null,
        [resourceState.collectionLoading]: true,
        [resourceState.collectionError]: null,
      };
    case actionTypes.FETCH_SUCCESS:
      if ((action.meta?.requestId ?? null) !== (state[resourceState.collectionRequestId] ?? null)) return state;
      return {
        ...state,
        [resourceState.collectionLoading]: false,
        [resourceState.collection]: action.payload.data[resource.collectionPayloadKey] || [],
      };
    case actionTypes.FETCH_FAIL:
      if ((action.meta?.requestId ?? null) !== (state[resourceState.collectionRequestId] ?? null)) return state;
      return {
        ...state,
        [resourceState.collectionLoading]: false,
        [resourceState.collectionError]: extractError(action),
      };

    case actionTypes.FETCH_ONE_BEGIN:
      return {
        ...state,
        [resourceState.currentLoading]: true,
        [resourceState.current]: null,
      };
    case actionTypes.FETCH_ONE_SUCCESS:
      return {
        ...state,
        [resourceState.currentLoading]: false,
        [resourceState.current]: action.payload.data[resource.detailPayloadKey] || null,
      };
    case actionTypes.FETCH_ONE_FAIL:
      return {
        ...state,
        [resourceState.currentLoading]: false,
        [resourceState.error]: extractError(action),
      };

    case actionTypes.CREATE_BEGIN:
    case actionTypes.UPDATE_BEGIN:
    case actionTypes.ACTIVATE_BEGIN:
    case actionTypes.DEACTIVATE_BEGIN:
      return {
        ...state,
        [resourceState.saving]: true,
        [resourceState.error]: null,
      };
    case actionTypes.CREATE_SUCCESS:
    case actionTypes.UPDATE_SUCCESS:
    case actionTypes.ACTIVATE_SUCCESS:
    case actionTypes.DEACTIVATE_SUCCESS:
      return {
        ...state,
        [resourceState.saving]: false,
      };
    case actionTypes.CREATE_FAIL:
    case actionTypes.UPDATE_FAIL:
    case actionTypes.ACTIVATE_FAIL:
    case actionTypes.DEACTIVATE_FAIL:
      return {
        ...state,
        [resourceState.saving]: false,
        [resourceState.error]: extractError(action),
      };
    default:
      return null;
  }
};

export default (state, action) => {
  const currentState = state || initialState;
  const patientGroupState = reducePatientGroupResource(currentState, action);
  if (patientGroupState) return patientGroupState;

  switch (action.type) {
    // ============ CAMPAIGNS ============
    case RECAPTURE_CAMPAIGNS.FETCH_BEGIN:
      return {
        ...state,
        campaignsLoading: true,
        campaignsError: null,
      };
    case RECAPTURE_CAMPAIGNS.FETCH_SUCCESS:
      return {
        ...state,
        campaignsLoading: false,
        campaigns: action.payload.data.campaigns || [],
      };
    case RECAPTURE_CAMPAIGNS.FETCH_FAIL:
      return {
        ...state,
        campaignsLoading: false,
        campaignsError: extractError(action),
      };

    case RECAPTURE_CAMPAIGNS.CREATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.UPDATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.ACTIVATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.DEACTIVATE_BEGIN:
    case RECAPTURE_CAMPAIGNS.REORDER_BEGIN:
      return {
        ...state,
        campaignSaving: true,
        campaignError: null,
      };
    case RECAPTURE_CAMPAIGNS.CREATE_SUCCESS:
    case RECAPTURE_CAMPAIGNS.UPDATE_SUCCESS:
    case RECAPTURE_CAMPAIGNS.ACTIVATE_SUCCESS:
    case RECAPTURE_CAMPAIGNS.DEACTIVATE_SUCCESS:
      return {
        ...state,
        campaignSaving: false,
      };
    case RECAPTURE_CAMPAIGNS.REORDER_SUCCESS:
      return {
        ...state,
        campaignSaving: false,
        campaigns: action.payload.data.campaigns || state.campaigns,
      };
    case RECAPTURE_CAMPAIGNS.CREATE_FAIL:
    case RECAPTURE_CAMPAIGNS.UPDATE_FAIL:
    case RECAPTURE_CAMPAIGNS.ACTIVATE_FAIL:
    case RECAPTURE_CAMPAIGNS.DEACTIVATE_FAIL:
    case RECAPTURE_CAMPAIGNS.REORDER_FAIL:
      return {
        ...state,
        campaignSaving: false,
        campaignError: extractError(action),
      };

    // ============ CAMPAIGN DRAFTS (ENG-3285) ============
    case RECAPTURE_CAMPAIGN_DRAFTS.FETCH_BEGIN:
      return {
        ...state,
        campaignDraftsLoading: true,
        campaignDraftsError: null,
      };
    case RECAPTURE_CAMPAIGN_DRAFTS.FETCH_SUCCESS:
      return {
        ...state,
        campaignDraftsLoading: false,
        campaignDrafts: action.payload.data.drafts || [],
      };
    case RECAPTURE_CAMPAIGN_DRAFTS.FETCH_FAIL:
      return {
        ...state,
        campaignDraftsLoading: false,
        campaignDraftsError: extractError(action),
      };

    case RECAPTURE_CAMPAIGN_DRAFTS.CREATE_BEGIN:
    case RECAPTURE_CAMPAIGN_DRAFTS.UPDATE_BEGIN:
    case RECAPTURE_CAMPAIGN_DRAFTS.DELETE_BEGIN:
      return {
        ...state,
        campaignDraftSaving: true,
        campaignDraftError: null,
      };
    // Create/update fold the returned draft into the list at the head — the
    // API orders by most-recently-edited first, so a save is a move-to-front,
    // and the list stays current without a refetch.
    case RECAPTURE_CAMPAIGN_DRAFTS.CREATE_SUCCESS:
    case RECAPTURE_CAMPAIGN_DRAFTS.UPDATE_SUCCESS: {
      const { draft } = action.payload.data;
      const others = state.campaignDrafts.filter((row) => row.id !== draft?.id);
      return {
        ...state,
        campaignDraftSaving: false,
        campaignDrafts: draft ? [draft, ...others] : state.campaignDrafts,
      };
    }
    // 204 with no body: the deleted id rides the action meta.
    case RECAPTURE_CAMPAIGN_DRAFTS.DELETE_SUCCESS:
      return {
        ...state,
        campaignDraftSaving: false,
        campaignDrafts: state.campaignDrafts.filter((row) => row.id !== action.meta?.id),
      };
    case RECAPTURE_CAMPAIGN_DRAFTS.CREATE_FAIL:
    case RECAPTURE_CAMPAIGN_DRAFTS.UPDATE_FAIL:
    case RECAPTURE_CAMPAIGN_DRAFTS.DELETE_FAIL:
      return {
        ...state,
        campaignDraftSaving: false,
        campaignDraftError: extractError(action),
      };

    // ============ SCHEDULES ============
    case RECAPTURE_SCHEDULES.FETCH_BEGIN:
      if (!requestCanBegin(currentState.schedulesRequestId, action)) return state;
      return {
        ...state,
        schedulesRequestId: action.meta?.requestId ?? null,
        schedulesLoading: true,
        schedulesError: null,
      };
    case RECAPTURE_SCHEDULES.FETCH_SUCCESS:
      if ((action.meta?.requestId ?? null) !== (state.schedulesRequestId ?? null)) return state;
      return {
        ...state,
        schedulesLoading: false,
        schedules: action.payload.data.schedules || [],
      };
    case RECAPTURE_SCHEDULES.FETCH_FAIL:
      if ((action.meta?.requestId ?? null) !== (state.schedulesRequestId ?? null)) return state;
      return {
        ...state,
        schedulesLoading: false,
        schedulesError: extractError(action),
      };

    case RECAPTURE_SCHEDULES.CREATE_BEGIN:
    case RECAPTURE_SCHEDULES.UPDATE_BEGIN:
    case RECAPTURE_SCHEDULES.ACTIVATE_BEGIN:
    case RECAPTURE_SCHEDULES.DEACTIVATE_BEGIN:
      return {
        ...state,
        scheduleSaving: true,
        scheduleError: null,
      };
    case RECAPTURE_SCHEDULES.CREATE_SUCCESS:
    case RECAPTURE_SCHEDULES.UPDATE_SUCCESS:
    case RECAPTURE_SCHEDULES.ACTIVATE_SUCCESS:
    case RECAPTURE_SCHEDULES.DEACTIVATE_SUCCESS:
      return {
        ...state,
        scheduleSaving: false,
      };
    case RECAPTURE_SCHEDULES.CREATE_FAIL:
    case RECAPTURE_SCHEDULES.UPDATE_FAIL:
    case RECAPTURE_SCHEDULES.ACTIVATE_FAIL:
    case RECAPTURE_SCHEDULES.DEACTIVATE_FAIL:
      return {
        ...state,
        scheduleSaving: false,
        scheduleError: extractError(action),
      };

    // ============ TEMPLATES ============
    case RECAPTURE_TEMPLATES.FETCH_BEGIN:
      if (!requestCanBegin(currentState.templatesRequestId, action)) return state;
      return {
        ...state,
        templatesRequestId: action.meta?.requestId ?? null,
        templatesLoading: true,
        templatesError: null,
      };
    case RECAPTURE_TEMPLATES.FETCH_SUCCESS:
      if ((action.meta?.requestId ?? null) !== (state.templatesRequestId ?? null)) return state;
      return {
        ...state,
        templatesLoading: false,
        templates: action.payload.data.templates || [],
      };
    case RECAPTURE_TEMPLATES.FETCH_FAIL:
      if ((action.meta?.requestId ?? null) !== (state.templatesRequestId ?? null)) return state;
      return {
        ...state,
        templatesLoading: false,
        templatesError: extractError(action),
      };

    case RECAPTURE_TEMPLATES.CREATE_BEGIN:
    case RECAPTURE_TEMPLATES.UPDATE_BEGIN:
    case RECAPTURE_TEMPLATES.ACTIVATE_BEGIN:
    case RECAPTURE_TEMPLATES.DEACTIVATE_BEGIN:
    case RECAPTURE_TEMPLATES.DUPLICATE_BEGIN:
      return {
        ...state,
        templateSaving: true,
        templateError: null,
      };
    case RECAPTURE_TEMPLATES.CREATE_SUCCESS:
    case RECAPTURE_TEMPLATES.UPDATE_SUCCESS:
    case RECAPTURE_TEMPLATES.ACTIVATE_SUCCESS:
    case RECAPTURE_TEMPLATES.DEACTIVATE_SUCCESS:
    case RECAPTURE_TEMPLATES.DUPLICATE_SUCCESS:
      return {
        ...state,
        templateSaving: false,
      };
    case RECAPTURE_TEMPLATES.CREATE_FAIL:
    case RECAPTURE_TEMPLATES.UPDATE_FAIL:
    case RECAPTURE_TEMPLATES.ACTIVATE_FAIL:
    case RECAPTURE_TEMPLATES.DEACTIVATE_FAIL:
    case RECAPTURE_TEMPLATES.DUPLICATE_FAIL:
      return {
        ...state,
        templateSaving: false,
        templateError: extractError(action),
      };

    // ============ CALL TEMPLATES ============
    case RECAPTURE_CALL_TEMPLATES.FETCH_BEGIN:
      if (!requestCanBegin(currentState.callTemplatesRequestId, action)) return state;
      return {
        ...state,
        callTemplatesRequestId: action.meta?.requestId ?? null,
        callTemplatesLoading: true,
        callTemplatesError: null,
      };
    case RECAPTURE_CALL_TEMPLATES.FETCH_SUCCESS:
      if ((action.meta?.requestId ?? null) !== (state.callTemplatesRequestId ?? null)) return state;
      return {
        ...state,
        callTemplatesLoading: false,
        callTemplates: action.payload.data.call_templates || [],
        callPromptProfiles: action.payload.data.prompt_profiles || [],
      };
    case RECAPTURE_CALL_TEMPLATES.FETCH_FAIL:
      if ((action.meta?.requestId ?? null) !== (state.callTemplatesRequestId ?? null)) return state;
      return {
        ...state,
        callTemplatesLoading: false,
        callTemplatesError: extractError(action),
      };

    case RECAPTURE_CALL_TEMPLATES.CREATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.UPDATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.ACTIVATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.DEACTIVATE_BEGIN:
    case RECAPTURE_CALL_TEMPLATES.DUPLICATE_BEGIN:
      return {
        ...state,
        callTemplateSaving: true,
        callTemplateError: null,
      };
    case RECAPTURE_CALL_TEMPLATES.CREATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.UPDATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.ACTIVATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.DEACTIVATE_SUCCESS:
    case RECAPTURE_CALL_TEMPLATES.DUPLICATE_SUCCESS:
      return {
        ...state,
        callTemplateSaving: false,
      };
    case RECAPTURE_CALL_TEMPLATES.CREATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.UPDATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.ACTIVATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.DEACTIVATE_FAIL:
    case RECAPTURE_CALL_TEMPLATES.DUPLICATE_FAIL:
      return {
        ...state,
        callTemplateSaving: false,
        callTemplateError: extractError(action),
      };

    // ============ SMS PROFILES ============
    case RECAPTURE_SMS_PROFILES.FETCH_BEGIN:
      if (!requestCanBegin(currentState.smsProfilesRequestId, action)) return state;
      return {
        ...state,
        smsProfilesRequestId: action.meta?.requestId ?? null,
        smsProfilesLoading: true,
        smsProfilesError: null,
      };
    case RECAPTURE_SMS_PROFILES.FETCH_SUCCESS:
      if ((action.meta?.requestId ?? null) !== (state.smsProfilesRequestId ?? null)) return state;
      return {
        ...state,
        smsProfilesLoading: false,
        smsProfiles: action.payload.data.sms_profiles || [],
      };
    case RECAPTURE_SMS_PROFILES.FETCH_FAIL:
      if ((action.meta?.requestId ?? null) !== (state.smsProfilesRequestId ?? null)) return state;
      return {
        ...state,
        smsProfilesLoading: false,
        smsProfilesError: extractError(action),
      };

    // ============ EMAIL SETTINGS ============
    case RECAPTURE_EMAIL_SETTINGS.FETCH_BEGIN:
      if (!requestCanBegin(state.emailSettingsRequestId, action)) return state;
      return {
        ...state,
        emailSettingsLoading: true,
        emailSettingsError: null,
        emailSettingsRequestId: action.meta?.requestId ?? null,
      };
    case RECAPTURE_EMAIL_SETTINGS.FETCH_SUCCESS:
      if (!emailSettingsRequestIsCurrent(state, action)) return state;
      return {
        ...state,
        emailSettingsLoading: false,
        emailSettings: action.payload.data.recapture_email_setting || state.emailSettings,
      };
    case RECAPTURE_EMAIL_SETTINGS.FETCH_FAIL:
      if (!emailSettingsRequestIsCurrent(state, action)) return state;
      return { ...state, emailSettingsLoading: false, emailSettingsError: extractError(action) };
    case RECAPTURE_EMAIL_SETTINGS.SAVE_BEGIN:
      return {
        ...state,
        emailSettingsLoading: false,
        emailSettingsRequestId: null,
        emailSettingsSaving: true,
        emailSettingsError: null,
      };
    case RECAPTURE_EMAIL_SETTINGS.SAVE_SUCCESS:
      return {
        ...state,
        emailSettingsLoading: false,
        emailSettingsRequestId: null,
        emailSettingsSaving: false,
        emailSettings: action.payload.data.recapture_email_setting || state.emailSettings,
      };
    case RECAPTURE_EMAIL_SETTINGS.SAVE_FAIL:
      return { ...state, emailSettingsSaving: false, emailSettingsError: extractError(action) };

    // ============ EMAIL TEMPLATES ============
    case RECAPTURE_EMAIL_TEMPLATES.FETCH_BEGIN:
      if (!requestCanBegin(state.emailTemplatesRequestId, action)) return state;
      return {
        ...state,
        emailTemplatesLoading: true,
        emailTemplatesError: null,
        emailTemplatesRequestId: action.meta?.requestId ?? null,
      };
    case RECAPTURE_EMAIL_TEMPLATES.FETCH_SUCCESS: {
      if (!emailTemplateListRequestIsCurrent(state, action)) return state;
      const emailTemplates = action.payload.data.recapture_email_templates || [];
      return {
        ...state,
        emailTemplatesLoading: false,
        emailTemplates,
        // Card previews are generated from the canonical revision, so a list
        // refresh must invalidate only entries whose revision actually moved.
        emailMemberPreviews: evictRevisedEmailMemberPreviews(
          state.emailMemberPreviews,
          state.emailTemplates,
          emailTemplates,
        ),
        emailMemberPreviewRequests: invalidateRevisedEmailMemberPreviewRequests(
          state.emailMemberPreviewRequests,
          state.emailTemplates,
          emailTemplates,
        ),
      };
    }
    case RECAPTURE_EMAIL_TEMPLATES.FETCH_FAIL:
      if (!emailTemplateListRequestIsCurrent(state, action)) return state;
      return { ...state, emailTemplatesLoading: false, emailTemplatesError: extractError(action) };

    case RECAPTURE_EMAIL_TEMPLATES.FETCH_ONE_BEGIN:
      if (!requestCanBegin(state.emailTemplateDetailRequestId, action)) return state;
      return {
        ...state,
        emailTemplateDetailRequestId: action.meta?.requestId || null,
        emailTemplateError: null,
      };
    case RECAPTURE_EMAIL_TEMPLATES.FETCH_ONE_SUCCESS:
      if (!emailTemplateDetailRequestIsCurrent(state, action)) return state;
      return {
        ...state,
        emailTemplateConflictCode: null,
        emailTemplateError: null,
      };
    case RECAPTURE_EMAIL_TEMPLATES.FETCH_ONE_FAIL:
      if (!emailTemplateDetailRequestIsCurrent(state, action)) return state;
      return {
        ...state,
        emailTemplateError: extractError(action),
      };
    case RECAPTURE_EMAIL_TEMPLATES.FETCH_ONE_INVALIDATE:
      if (!requestCanBegin(state.emailTemplateDetailRequestId, action)) return state;
      return {
        ...state,
        emailTemplateDetailRequestId: action.meta?.requestId || null,
        emailTemplateError: null,
        emailTemplateConflictCode: null,
      };

    case RECAPTURE_EMAIL_TEMPLATES.CREATE_BEGIN:
    case RECAPTURE_EMAIL_TEMPLATES.UPDATE_BEGIN:
    case RECAPTURE_EMAIL_TEMPLATES.ACTIVATE_BEGIN:
    case RECAPTURE_EMAIL_TEMPLATES.DEACTIVATE_BEGIN:
      return {
        ...state,
        emailTemplateSaving: true,
        emailTemplateError: null,
        emailTemplateConflictCode: null,
      };
    case RECAPTURE_EMAIL_TEMPLATES.CREATE_SUCCESS:
      return {
        ...state,
        emailTemplateSaving: false,
        emailTemplateConflictCode: null,
      };
    case RECAPTURE_EMAIL_TEMPLATES.UPDATE_SUCCESS: {
      const updatedTemplateId = action.meta?.templateId
        || action.payload.data.recapture_email_template?.id;
      const emailMemberPreviews = { ...state.emailMemberPreviews };
      if (updatedTemplateId != null) delete emailMemberPreviews[updatedTemplateId];
      const emailMemberPreviewRequests = { ...state.emailMemberPreviewRequests };
      if (updatedTemplateId != null) {
        emailMemberPreviewRequests[updatedTemplateId] = {
          revisionId: action.payload.data.recapture_email_template?.current_revision_id || null,
          requestId: null,
        };
      }
      return {
        ...state,
        emailTemplateSaving: false,
        emailTemplateConflictCode: null,
        // A card preview is rendered from the template's canonical revision,
        // so its cached HTML is stale after a successful revision update.
        emailMemberPreviews,
        emailMemberPreviewRequests,
      };
    }
    case RECAPTURE_EMAIL_TEMPLATES.ACTIVATE_SUCCESS:
    case RECAPTURE_EMAIL_TEMPLATES.DEACTIVATE_SUCCESS:
      return { ...state, emailTemplateSaving: false, emailTemplateConflictCode: null };
    case RECAPTURE_EMAIL_TEMPLATES.CREATE_FAIL:
    case RECAPTURE_EMAIL_TEMPLATES.UPDATE_FAIL:
    case RECAPTURE_EMAIL_TEMPLATES.ACTIVATE_FAIL:
    case RECAPTURE_EMAIL_TEMPLATES.DEACTIVATE_FAIL:
      return {
        ...withEmailMutationFailure(state, action, 'emailTemplateError', 'emailTemplateConflictCode'),
        emailTemplateSaving: false,
      };

    case RECAPTURE_EMAIL_TEMPLATES.MEMBER_PREVIEW_BEGIN: {
      if (!emailMemberPreviewRequestCanBegin(state, action)) return state;
      const templateId = action.meta?.templateId;
      return {
        ...state,
        emailMemberPreviews: {
          ...state.emailMemberPreviews,
          [templateId]: { status: 'loading', html: null, error: null },
        },
        emailMemberPreviewRequests: {
          ...state.emailMemberPreviewRequests,
          [templateId]: {
            revisionId: action.meta?.revisionId,
            requestId: action.meta?.requestId,
          },
        },
      };
    }
    case RECAPTURE_EMAIL_TEMPLATES.MEMBER_PREVIEW_SUCCESS: {
      if (!emailMemberPreviewRequestIsCurrent(state, action)) return state;
      const templateId = action.meta?.templateId;
      const preview = action.payload.data.recapture_email_preview;
      return {
        ...state,
        emailMemberPreviews: {
          ...state.emailMemberPreviews,
          [templateId]: {
            status: preview?.html_body ? 'ready' : 'empty',
            html: preview?.html_body || null,
            subject: preview?.subject || null,
            error: null,
          },
        },
      };
    }
    case RECAPTURE_EMAIL_TEMPLATES.MEMBER_PREVIEW_FAIL: {
      if (!emailMemberPreviewRequestIsCurrent(state, action)) return state;
      const templateId = action.meta?.templateId;
      return {
        ...state,
        emailMemberPreviews: {
          ...state.emailMemberPreviews,
          [templateId]: { status: 'error', html: null, error: extractError(action) },
        },
      };
    }

    // ============ CLEAR ERRORS ============
    case CLEAR_RECAPTURE_ERRORS:
      return {
        ...state,
        campaignsError: null,
        campaignError: null,
        campaignDraftsError: null,
        campaignDraftError: null,
        schedulesError: null,
        scheduleError: null,
        templatesError: null,
        templateError: null,
        emailTemplatesError: null,
        emailTemplateError: null,
        emailTemplateConflictCode: null,
        callTemplatesError: null,
        callTemplateError: null,
        codeGroupsError: null,
        codeGroupError: null,
        appointmentTypeGroupsError: null,
        appointmentTypeGroupError: null,
        smsProfilesError: null,
      };

    default:
      return currentState;
  }
};
